import { useEffect, useState, useRef, useContext } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { PlantLayoutContext } from '../..';
import { apiUriConsumableDataHttp, apiUriDesignDrawingsHttp, apiUriMasterDataHttp } from '../../services/conf/apiConf';
import {
  fetchAllDataGet_byApiName,
  fetchAllDataGet_byApiName_for_RFQ,
  fetchAllDataGetFromAppFile_byApiName,
} from '../../services/gets/getdata';
import {
  sendAllDataPost_byApiNameAndData,
  sendAllFileByPost,
  pollForFile,
  getFileByGuid_Post,
} from '../../services/posts/postdata';
import linkExceptions from '../../services/Utils/linkExceptions';
import { ConsumableLifetimeCycleProps } from './consumable-lifetime-cycle';
import help from './consumable-lifetime-cycle.help';
import translations from './i18n';

function useConsumableLifetimeCycleHook(props: ConsumableLifetimeCycleProps) {
  useResourceBundle(translations, undefined, '84e3446d-d3e2-42b4-badd-5e39837352bf');
  useHelpBundle(help);
  const userRoles = JSON.parse(sessionStorage.getItem('UserRoles') || '');
  const isAdmin = sessionStorage.getItem('superUserRole');

  const { t } = useTranslation();

  const [dataFromPlantLayout, setDataFromPlantLayout] = useState<any[]>([]);
  const [dataFromPlantConfigdata, setDataFromPlantConfigdata] = useState([]);
  const [dataForTable, setdataForTable] = useState<any[]>([]);
  const [dataForTableCopy, setdataForTableCopy] = useState<any[]>([]);

  const [pageNumber, setpageNumber] = useState(1);
  const [totalPage, settotalPage] = useState(1);
  const [clearSelection, setclearSelection] = useState(false);
  const [showConsumableCodeModal, setshowConsumableCodeModal] = useState(false);
  const [showConsumableCodeDrawing, setshowConsumableCodeDrawing] = useState(false);
  const [showUploadAllConsumablesModal, setShowUploadAllConsumablesModal] = useState(false);
  const [isCancelUploading, setIsCancelUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [totalFileCount, setTotalFileCount] = useState<number>(0);
  const [processedFileCount, setProcessedFileCount] = useState<number>(0);
  const [selectedCode, setselectedCode] = useState<string>('');
  const [selectedEquipment, setselectedEquipment] = useState<string>('');
  const [selectedEquipmentId, setselectedEquipmentId] = useState<string>('');
  const [allGuidData, setallGuidData] = useState([]);
  const [allGuidDataForEquipment, setallGuidDataForEquipment] = useState([]);
  const [selectedGuId, setSelectedGuId] = useState<string>('');
  const [iFrameSrc, setIframeSrc] = useState('');
  const [searchLogoParameter, setsearchLogoParameter] = useState<string>('');
  const [lifetimeInput, setLifetimeInput] = useState<string>();
  const [lifetimeInputLast, setLifetimeInputLast] = useState<string>();
  const [waitingForExcelFile, setwaitingForExcelFile] = useState(false);

  const [searchVisible, setSearchVisible] = useState(false);
  const [searchState, setSearchState] = useState({});
  const [currentDataForTable, setCurrentDataForTable] = useState<any[]>([]);

  const [editlifeandcycle, setEditLifeAndCycle] = useState<boolean>(false);
  const [globalLife, setGlobalLife] = useState('');
  const [globalCycle, setGlobalCycle] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawingLoading, setIsDrawingLoading] = useState(false);

  const isCancelUploadingRef = useRef<boolean>(false);
  const totalDataInOnePage = 100;
  const plantLayoutFromMemory = useContext(PlantLayoutContext);

  const searchLogoParamOptions = [
    { display: 'Equal to (=)', value: 'Equal to' },
    { display: 'Greater than (>)', value: 'Greater than' },
    { display: 'Less than (<)', value: 'Less than' },
    { display: '(In Between)', value: 'In Between' },
  ];

  useEffect(() => {
    fetchInitialData();
    fetchInitialDataForPdf();
  }, []);

  const fetchInitialData = async () => {
    await fetchAllDataGet_byApiName('GetPlantConfigData', apiUriMasterDataHttp()).then((response) => {
      const filteredData = response.consumableMaster.filter(
        (item) => item.lifetime !== 1 && item.consumablecodeext !== 'N/A',
      );
      setDataFromPlantConfigdata(filteredData);
      setIsLoading(false);
    });
    setDataFromPlantLayout(plantLayoutFromMemory?.layoutData ?? []);
  };

  const fetchInitialDataForPdf = async () => {
    await fetchAllDataGetFromAppFile_byApiName('GetConsumableItemDocumentGuid', apiUriDesignDrawingsHttp())
      .then((data) => {
        setallGuidData(data);
      })
      .catch((error) => {
        console.log('Failed to Fetch Data');
      });

    await fetchAllDataGetFromAppFile_byApiName('GetEquipmentDocumentGuids', apiUriDesignDrawingsHttp())
      .then((data) => {
        setallGuidDataForEquipment(data);
      })
      .catch((error) => {
        console.log('Failed to Fetch Data');
      });
  };

  useEffect(() => {
    const arrangedData: any = dataFromPlantConfigdata?.map((oneData: any) => {
      const dataFiltered: any = dataFromPlantLayout?.find((oneItem: any) => oneItem.displayseq === oneData.lineseq);
      const dataToSend = {
        displayseq: oneData?.displayseq.toString(),
        lineseq: oneData?.lineseq.toString(),
        areacode: dataFiltered?.areacode,
        zonecode: dataFiltered?.zonecode,
        equipmentcode: dataFiltered?.equipmentcode,
        equipmentid: dataFiltered?.equipmentid,
        drawingNumber: dataFiltered?.drawingnumber,
        consumabledescription: oneData?.consumabledescription,
        consumablecode: oneData?.consumablecodeext,
        companyConsumableCode: oneData?.companyConsumableCode ?? 'N/A',
        companyConsumableDesc: oneData?.companyConsumableDesc ?? 'N/A',
        // lifetime: oneData?.lifetime.toString(),
        lifetime: oneData?.lifetime === 1 ? ' ' : oneData?.lifetime.toString(),
        cycles: oneData?.redressedCycle.toString(),
        // maxCycles: oneData?.redressedCycleMax.toString()
        maxCycles: oneData?.lifetime === 1 ? ' ' : oneData?.redressedCycleMax?.toString(),
      };
      return dataToSend;
    });

    setdataForTableCopy(arrangedData);
    setdataForTable(arrangedData);
    handleSearch(undefined, undefined, arrangedData);
  }, [dataFromPlantLayout, dataFromPlantConfigdata]);

  useEffect(() => {
    const totalPageNum = Math.ceil(dataForTable.length / totalDataInOnePage);
    settotalPage(totalPageNum);
  }, [dataForTable]);

  const handleLifeCycleEditModalCSave = async () => {
    setIsLoading(true);
    const dataTosave = {
      lifetime: globalLife,
      redressedCycleMax: globalCycle,
      consumableCodes: dataForTable.map((tableDataItem) => {
        return tableDataItem.consumablecode;
      }),
    };
    // console.log(dataTosave);
    await sendAllDataPost_byApiNameAndData('UpdateConsumableLifecycle', dataTosave, apiUriConsumableDataHttp())
      .then((res) => {
        setIsLoading(false);
        notification.success('Success', `${dataTosave.consumableCodes.length} Items Updated successfully!`);
        fetchInitialData();
        fetchInitialDataForPdf();
      })
      .catch((error) => {
        setIsLoading(false);
        notification.error('Error', `${dataTosave.consumableCodes.length} Items failed update!`);
      });

    handleLifeCycleEditModalClose();
  };

  const handleLifeCycleEditModalClose = () => {
    setGlobalCycle('');
    setGlobalLife('');
    setclearSelection(true);
    setEditLifeAndCycle(false);
  };

  const handleToggleSearch = (currentState: boolean) => {
    setdataForTable(dataForTableCopy);
    setSearchState({});
    setSearchVisible(!currentState);
  };

  const handleSearch = (searchString?: any, columnName?: string, dataToFilter?: any[]) => {
    let searchState_local = {};
    if (!searchString) {
      searchState_local = searchState;
    } else {
      const searchValue = searchString.toLowerCase();
      setSearchState((prevState) => {
        const newState = { ...prevState };
        if (searchValue || searchValue === '') {
          newState[columnName || 'global'] = searchValue;
        } else {
          delete newState[columnName || 'global'];
        }
        searchState_local = newState;
        return newState;
      });
    }

    const filterData = (data: any[], searchState: any) => {
      return data.filter((item) => {
        return Object.entries(searchState).every(([key, value]) => {
          if (key === 'global') {
            return Object.values(item).some((itemValue: any) => itemValue.toLowerCase().includes(value));
          }
          return item[key]?.toLowerCase().includes(value);
        });
      });
    };

    const searchedData = filterData(dataToFilter || dataForTableCopy, searchState_local);

    if (searchedData && searchedData.length > 0) {
      setdataForTable(searchedData);
    } else if (searchState && columnName) {
      notification.info('Info', `${searchString} in ${columnName} Not Found`);
    }
  };

  const handleSearchLogoParamOptions = (value: string) => {
    setsearchLogoParameter(value);
  };

  const handleLifetimeInputChange = (value: any) => {
    setLifetimeInput(value.target.value);
  };

  const handleLifetimeInputLastChange = (value: any) => {
    setLifetimeInputLast(value.target.value);
  };

  const handleFilterByLifetime = (searchParam: any, lastlifetime: any) => {
    const value = Number(searchParam);
    const lastLifetime = Number(lastlifetime);

    if (!isNaN(value)) {
      if (searchLogoParameter === 'Greater than') {
        const filteredData = dataForTableCopy?.filter((item: any) => Number(item.lifetime) > value);
        if (filteredData.length > 0) {
          setdataForTable(filteredData);
        } else {
          notification.error(
            'Not Found !',
            `Record with Lifetime>${lifetimeInput} does not exist`,
            undefined,
            'medium',
            3,
          );
        }
      } else if (searchLogoParameter === 'Less than') {
        if (value > 1) {
          const filteredData = dataForTableCopy?.filter((item: any) => Number(item.lifetime) < value);
          if (filteredData.length > 0) {
            setdataForTable(filteredData);
          } else {
            notification.error(
              'Not Found !',
              `Record with Lifetime<${lifetimeInput} does not exist`,
              undefined,
              'medium',
              3,
            );
          }
        } else if (searchParam) {
          notification.warning('Alert !', 'Please enter a greater number.', undefined, 'medium', 3);
        } else {
          setdataForTable(dataForTableCopy);
        }
      } else if (searchLogoParameter === 'Equal to') {
        if (searchParam) {
          const filteredData = dataForTableCopy?.filter((item: any) => Number(item.lifetime) === value);
          if (filteredData.length > 0) {
            setdataForTable(filteredData);
          } else {
            notification.error(
              'Not Found !',
              `Record with Lifetime=${lifetimeInput} does not exist`,
              undefined,
              'medium',
              3,
            );
          }
        } else {
          setdataForTable(dataForTableCopy);
        }
      } else if (searchLogoParameter === 'In Between') {
        if (searchParam && lastlifetime) {
          const filteredData = dataForTableCopy?.filter(
            (item: any) => Number(item.lifetime) >= value && Number(item.lifetime) <= lastLifetime,
          );
          if (filteredData.length > 0) {
            setdataForTable(filteredData);
          } else {
            notification.error(
              'Not Found !',
              `Record with Lifetime range ${lifetimeInput}-${lifetimeInputLast} does not exist`,
              undefined,
              'medium',
              3,
            );
          }
        } else {
          setdataForTable(dataForTableCopy);
        }
      } else {
        setdataForTable(dataForTableCopy);
        notification.warning('Alert !', 'Please select a filter parameter.', undefined, 'medium', 3);
      }
    } else {
      notification.warning('Alert !', 'Please enter a valid number.', undefined, 'medium', 3);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPage) {
      setpageNumber(pageNumber + 1);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setpageNumber(pageNumber - 1);
    }
  };

  const handleFirstPage = () => {
    setpageNumber(1);
  };

  const handleLastPage = () => {
    setpageNumber(totalPage);
  };

  useEffect(() => {
    const startIndex = (pageNumber - 1) * totalDataInOnePage;
    const endIndex = startIndex + totalDataInOnePage;
    setCurrentDataForTable(dataForTable.slice(startIndex, endIndex));
  }, [pageNumber, dataForTable, totalDataInOnePage]);

  const handleSaveDeleteFromTable = (data: any, returnType: any) => {
    data.displayseq = parseFloat(data.displayseq);
    data.lineseq = parseFloat(data.lineseq);
    data.lifetime = parseFloat(data.lifetime);
    data.cycles = parseFloat(data.cycles);
    data.maxCycles = parseFloat(data.maxCycles);

    const dataToSend = {
      displayseq: data.displayseq,
      consumablecodeext: data.consumablecode,
      consumabledescription: data.consumabledescription,
      lineseq: data.lineseq,
      billetid: '',
      lifetime: data.lifetime,
      redressedCycle: data.cycles,
      redressedCycleMax: data.maxCycles,
      quantityinstalled: 0,
      quantityredressed: 0,
      isactive: true,
      remarks: '',
      productprofiles: [
        {
          producttype: '',
          productdescription: '',
        },
      ],
    };

    if (returnType === 'edit') {
      if (dataToSend.redressedCycle <= dataToSend.redressedCycleMax) {
        sendAllDataPost_byApiNameAndData('UpdateConsumableLife', dataToSend, apiUriConsumableDataHttp())
          .then((response) => {
            if (response) {
              fetchInitialData();
              notification.success('Success !', 'Data saved successfully', undefined, 'medium', 4);
            }
          })
          .catch((error) => {
            notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 2);
          });
      } else {
        fetchInitialData();
        notification.error('Error !', 'Current Cycle is greater than Max. Cycle', undefined, 'medium', 4);
      }
    }
  };

  // Loads the drawing into the iframe; false when it can't be fetched.
  async function fetchAndSetIframeSource(guId: string, apiName: string, baseUri?: string): Promise<boolean> {
    setSelectedGuId(guId);
    const queryParams = new URLSearchParams({ guid: guId });
    try {
      const blob: any = await pollForFile(`${apiName}?${queryParams}`, {}, undefined, undefined, baseUri);
      const blobUrl = URL.createObjectURL(blob);
      setIframeSrc(blobUrl);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  const openDrawing = async (guId: string | undefined, apiName: string) => {
    setIsDrawingLoading(true);
    try {
      const found = guId ? await fetchAndSetIframeSource(guId, apiName, apiUriDesignDrawingsHttp()) : false;
      if (found) {
        setshowConsumableCodeDrawing(true);
      } else {
        setSelectedGuId('');
        setshowConsumableCodeModal(true);
      }
    } finally {
      setIsDrawingLoading(false);
    }
  };

  const handleSelection = (selectedRowIndex: any, selectedRowData: any, event: any, columnName: any) => {
    const isConsumableCodeClick =
      selectedRowData.consumablecode === event.target.textContent && columnName.accessor === 'consumablecode';
    const isCompanyCodeClick =
      selectedRowData.companyConsumableCode === event.target.textContent &&
      columnName.accessor === 'companyConsumableCode';
    if (
      (isConsumableCodeClick || isCompanyCodeClick) &&
      event.target.tagName.toLowerCase() === 'div' &&
      !linkExceptions.includes(event.target.textContent)
    ) {
      // Several company codes for one consumable are shown comma-separated; the drawing belongs to the first one
      const code: string = isCompanyCodeClick
        ? String(selectedRowData.companyConsumableCode).split(',')[0].trim()
        : String(selectedRowData.consumablecode);

      const guidForSelectedCode: any = allGuidData?.find((item: any) => item.docfor === code);
      setselectedCode(code);
      openDrawing(guidForSelectedCode?.guid?.[0], 'getConsumableItemDesign');
    } else if (
      selectedRowData.equipmentcode === event.target.textContent &&
      event.target.tagName.toLowerCase() === 'div' &&
      columnName.accessor === 'equipmentcode' &&
      !linkExceptions.includes(event.target.textContent)
    ) {
      const guidForSelectedEquipment: any = allGuidDataForEquipment?.find(
        (item: any) => item.docfor === selectedRowData.drawingNumber,
      );
      setselectedEquipment(selectedRowData.equipmentcode);
      setselectedEquipmentId(selectedRowData.drawingNumber);
      openDrawing(guidForSelectedEquipment?.guid?.[0], 'GetEquipmentDesign');
    } else if (Object.keys(searchState).length !== 0 && !Object.values(searchState).every((value) => value === '')) {
      setGlobalCycle(selectedRowData.maxCycles || '');
      setGlobalLife(selectedRowData.lifetime || '');
      setEditLifeAndCycle(true);
    }
    setclearSelection(false);
  };

  const handleModalCancel = () => {
    setselectedCode('');
    setselectedEquipment('');
    setselectedEquipmentId('');
    setclearSelection(true);
    setshowConsumableCodeModal(false);
  };

  const handleDrawingModalCancel = () => {
    setIframeSrc('');
    setselectedCode('');
    setselectedEquipment('');
    setselectedEquipmentId('');
    setSelectedGuId('');
    setclearSelection(true);
    setshowConsumableCodeDrawing(false);
  };

  const handleUploadConsumableDrawingsModalCancel = () => {
    isCancelUploadingRef.current = true;
    setTotalFileCount(0);
    setProcessedFileCount(0);
    setShowUploadAllConsumablesModal(false);
  };

  const handleFileUpload = (event: any) => {
    const file = event.target.files[0];
    const allowedTypes = ['application/pdf'];
    if (file && allowedTypes?.includes(file.type)) {
      setSelectedFile(file);
    } else {
      event.target.value = '';
      alert('Please Select A PDF File.');
    }
  };

  const handleFolderSelection = async () => {
    try {
      const dirHandle = await window.showDirectoryPicker();
      let count = 0;

      // First loop to count the number of PDF files
      for await (const entry of dirHandle.values()) {
        if (entry.kind === 'file') {
          const file = await entry.getFile();
          if (file.type === 'application/pdf') {
            count++;
          }
        }
      }
      setTotalFileCount(count);
      setProcessedFileCount(0);
      setShowUploadAllConsumablesModal(true);

      // Second loop to process each PDF file
      for await (const entry of dirHandle.values()) {
        if (isCancelUploadingRef.current) {
          notification.error('Cancelled!', `Uploading cancelled`, undefined, 'medium', 3);
          break;
        }

        if (entry.kind === 'file') {
          const file = await entry.getFile();
          if (file.type === 'application/pdf') {
            await processFile(file); // Process the file immediately
            setProcessedFileCount((prevCount) => prevCount + 1);
          }
        }
      }
      handleUploadConsumableDrawingsModalCancel();
      fetchInitialDataForPdf();
      isCancelUploadingRef.current = false;
    } catch (error) {
      console.error('Error selecting folder:', error);
    }
  };

  const progressPercentage = totalFileCount > 0 ? Math.round((processedFileCount / totalFileCount) * 100) : 0;

  const processFile = async (file: File) => {
    const formData = new FormData();
    formData.append('PlantCode', '');
    formData.append('Files', file);

    try {
      const response = await sendAllFileByPost('uploadFile', formData);
      if (response) {
        notification.success('Upload Successful', `${file.name} uploaded successfully.`, undefined, 'medium', 1);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      notification.error('Error!', `Failed to upload ${file.name}`, undefined, 'medium', 3);
    }
  };

  const handleSaveFromModal = () => {
    const formData = new FormData();
    if (selectedFile) {
      formData.append('File', selectedFile);
      if (selectedCode.trim().length > 0) {
        formData.append('ConsumableCode', selectedCode);
      } else if (selectedEquipment.trim().length > 0) {
        formData.append('EquipmentCode', selectedEquipmentId);
      }

      const apiName: string = selectedCode.trim().length > 0 ? 'uploadConsumableItemDesign' : 'UploadEquipmentDesign';

      sendAllFileByPost(apiName, formData, undefined, apiUriDesignDrawingsHttp())
        .then((response) => {
          if (response) {
            fetchInitialDataForPdf();
            notification.success('Success !', 'File uploaded successfully', undefined, 'medium', 2);
          }
        })
        .catch((error) => {
          console.log(error);
          notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 2);
        });
    }

    handleModalCancel();
  };

  const handleDeletePDF = () => {
    const queryParams = new URLSearchParams({
      guid: selectedGuId,
    });

    const apiName = selectedCode.trim().length > 0 ? 'DeleteConsumableItemDesign' : 'DeleteEquipmentDesign';

    getFileByGuid_Post(`${apiName}?${queryParams}`, {}, apiUriDesignDrawingsHttp())
      .then((response) => {
        if (response) {
          fetchInitialDataForPdf();
          setIframeSrc('');
          notification.success('Removed !', 'File Deleted Permanently', undefined, 'medium', 3);
        }
      })
      .catch((error) => {
        console.log(error);
        notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
      });
  };

  const downloadExcelFile = async () => {
    setwaitingForExcelFile(true);
    notification.info('Request Sent For File', 'Waiting...', t, 'medium', 50);

    try {
      const { headers, data } = await fetchAllDataGet_byApiName_for_RFQ(
        `GenerateConsumableLifetimeReport`,
        apiUriConsumableDataHttp(),
      );

      // Access the Content-Disposition header
      const contentDisposition = headers.get('Content-Disposition');
      let filename = 'default_filename.xlsx'; // Default to .xlsx

      if (contentDisposition) {
        // Improved regex to match different filename formats
        const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, ''); // Remove any surrounding quotes
        }
      }

      // Determine the MIME type based on the file extension
      let mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; // Default to .xlsx
      if (filename.endsWith('.xls')) {
        mimeType = 'application/vnd.ms-excel';
      } else if (filename.endsWith('.xlsm')) {
        mimeType = 'application/vnd.ms-excel.sheet.macroEnabled.12';
      } else if (filename.endsWith('.xlsb')) {
        mimeType = 'application/vnd.ms-excel.sheet.binary.macroEnabled.12';
      } else if (filename.endsWith('.csv')) {
        mimeType = 'text/csv';
      } else if (filename.endsWith('.ods')) {
        mimeType = 'application/vnd.oasis.opendocument.spreadsheet';
      }

      // Convert base64 string back to binary data
      const byteCharacters = atob(data.split(',')[1]); // Remove the data URL prefix
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);

      // Create a Blob from the binary data
      const blob = new Blob([byteArray], { type: mimeType });

      // Create a link element
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      // Append the link to the document body and trigger the download
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      document.body.removeChild(link);
      setwaitingForExcelFile(false);
    } catch (error) {
      console.log('Failed to Fetch Data:', error);
      setwaitingForExcelFile(false);
      notification.error('Error!', 'Unsuccessful Attempt', undefined, 'medium', 2);
    }
  };

  return {
    t,
    dataForTable,
    currentDataForTable,
    pageNumber,
    totalPage,
    isLoading,
    isDrawingLoading,
    showConsumableCodeModal,
    showConsumableCodeDrawing,
    iFrameSrc,
    selectedCode,
    selectedEquipment,
    selectedEquipmentId,
    clearSelection,
    searchLogoParameter,
    searchLogoParamOptions,
    lifetimeInput,
    lifetimeInputLast,
    waitingForExcelFile,
    searchVisible,
    editlifeandcycle,
    globalLife,
    globalCycle,
    dataForTableCopy,
    showUploadAllConsumablesModal,
    totalFileCount,
    processedFileCount,
    progressPercentage,
    handleLifeCycleEditModalCSave,
    handleToggleSearch,
    handleLifeCycleEditModalClose,
    setGlobalCycle,
    setGlobalLife,
    setEditLifeAndCycle,
    setSearchVisible,
    handleNextPage,
    handlePreviousPage,
    handleFirstPage,
    handleLastPage,
    handleSearch,
    handleFilterByLifetime,
    handleSearchLogoParamOptions,
    handleLifetimeInputChange,
    handleLifetimeInputLastChange,
    handleSaveDeleteFromTable,
    handleSelection,
    handleModalCancel,
    handleDrawingModalCancel,
    handleSaveFromModal,
    handleFileUpload,
    handleDeletePDF,
    downloadExcelFile,
    handleUploadConsumableDrawingsModalCancel,
    handleFolderSelection,
    userRoles,
    isAdmin,
  };
}

export default useConsumableLifetimeCycleHook;
